import { useMemo } from 'react'
import useSWRInfinite from 'swr/infinite'
import { type AdminContext, useAdmin } from '@vtex/raccoon-next'
import { fetcher } from '@/lib/fetcher'

const DEFAULT_SELLER_ID = '1'

type SellerRegister = {
  paging: {
    from: number
    to: number
    total: number
  }
  items: {
    id: string
    name: string
  }[]
}

const PAGE_SIZE = 10

const getKey =
  (adminContext: AdminContext, keyword: string) =>
  (pageIndex = 0, previousPageData: SellerRegister) => {
    if (
      previousPageData &&
      previousPageData.paging.to > previousPageData.paging.total
    ) {
      return null
    }

    const nextIndex = pageIndex * PAGE_SIZE

    return {
      ...adminContext,
      raccoonUrl: adminContext.production
        ? adminContext.prodUrl
        : adminContext.devUrl,
      url: `/api/proxy/seller-register/pvt/sellers?an=${adminContext.account}&from=${nextIndex}&to=${nextIndex + PAGE_SIZE}&keyword=${keyword}&sort=name:asc`,
    }
  }

export const useSellerWithPagination = (
  search = '',
  removeDefaultSeller = true
) => {
  const adminContext = useAdmin()
  const { data, setSize, isLoading, ...rest } = useSWRInfinite<SellerRegister>(
    getKey(adminContext, search),
    fetcher,
    { revalidateFirstPage: false }
  )

  const sellers = useMemo(() => {
    if (isLoading) return []

    const items = data?.flatMap((page) => page.items) ?? []

    if (!removeDefaultSeller) return items

    return items.filter((seller) => seller.id !== DEFAULT_SELLER_ID)
  }, [data, isLoading, removeDefaultSeller])

  const hasMore = useMemo(() => {
    const lastPage = data?.at(-1)

    if (!lastPage) return false

    return lastPage.paging.to < lastPage.paging.total
  }, [data])

  return {
    ...rest,
    sellers,
    isLoading,
    hasMore,
    loadMore: () => setSize((currentValue) => currentValue + 1),
  }
}
